import {
    AccountVerificationStatus,
    ClaimType,
    HsabExpenseDisplayStatus,
    ExpenseCategory,
    FormDocumentPlan,
    FormDocumentType,
    HsabAccountNametag,
    HsabAccountType,
    HsabDocumentType,
    HsabExpenseCategory,
    HsabExpenseOriginator,
    HsabPlanSubType,
    HsabPlanType,
    LinkedBankAccountUse,
    LinkedBankAccountUseStatus,
    MassTransitManualClaimStatus,
    ResourceFormPlanType
} from "@/shared/types";

export const planSubTypeIconMap: Record<HsabPlanSubType, string> = {
    none: "",
    retirementHra: "fa-handshake",
    lifestyleSpendingAccount: "fa-dumbbell",
    wellnessHra: "fa-apple-whole",
    adoptionFsa: "fa-baby",
    limitedPurposeFsa: "fa-tooth",
    limitedPurposeHra: "fa-kit-medical",
    postDeductibleFsa: "fa-kit-medical",
    educationHra: "fa-user-graduate"
};
export const resourceFormPlanTypeIconMap: Record<ResourceFormPlanType, string> = {
    unknown: "fa-question",
    medicalFlex: "fa-kit-medical",
    dependentCare: "fa-child-reaching",
    healthReimbursementAccount: "fa-hand-holding-heart",
    healthSavingAccount: "fa-piggy-bank",
    commuterParking: "fa-car",
    commuterTransit: "fa-train-subway",
    retirementHra: "fa-handshake",
    lifestyleSpendingAccount: "fa-dumbbell",
    wellnessHra: "fa-apple-whole",
    adoptionFsa: "fa-baby",
    limitedPurposeFsa: "fa-tooth",
    educationHra: "fa-user-graduate",
    limitedPurposeHra: "fa-kit-medical",
    postDeductibleFsa: "fa-kit-medical",
    healthReimbursementAccountDeductibles: "fa-question",
    healthReimbursementAccountDental: "fa-question",
    healthReimbursementAccountVision: "fa-question",
    individualPremium: "fa-question",
    voluntaryEmployeesBeneficiaryAssociation: "fa-question"
};
export const linkedBankAccountUseIconMap: Record<LinkedBankAccountUse, string> = {
    unspecified: "fa-question",
    directDeposit: "fa-money-bill-transfer",
    reimbursementDebitCard: "fa-credit-card",
    vebaWithdrawals: "fa-money-check-dollar",
    other: "fa-money-check-dollar"
};
export const linkedBankAccountUseStatusIconMap: Record<LinkedBankAccountUseStatus, string> = {
    unspecified: "fa-light fa-clock",
    inactive: "fa-light fa-circle-exclamation",
    active: "fa-light fa-circle-check",
    pending: "fa-light fa-clock"
};
export const linkedBankAccountUseStatusColorMap: Record<LinkedBankAccountUseStatus, string> = {
    unspecified: "color-tertiary",
    inactive: "color-red",
    active: "color-primary",
    pending: "color-red"
};
export const linkedBankAccountUseStatusTitleMap: Record<LinkedBankAccountUseStatus, string> = {
    unspecified: "The setup process has not been completed for this account.",
    inactive: "This account is inactive and should be removed.",
    active: "",
    pending: "This account is in the approval process. It may take up to seven days to connect your bank account for reimbursement."
};
export const accountVerificationStatusIconMap: Record<AccountVerificationStatus, string> = {
    none: "fa-light fa-question-circle",
    pending: "fa-light fa-clock",
    verified: "fa-light fa-circle-check",
    failed: "fa-light fa-exclamation-triangle",
    expired: "fa-light fa-circle-xmark",
    confirmationRequired: "fa-light fa-shield-check"
};
export const planTypeIconMap: Record<HsabPlanType, string> = {
    unknown: "fa-question",
    medicalFlex: "fa-kit-medical",
    dependentCare: "fa-child-reaching",
    healthReimbursementAccount: "fa-hand-holding-heart",
    healthSavingAccount: "fa-piggy-bank",
    commuterParking: "fa-car",
    commuterTransit: "fa-train-subway",
};
const claimTypeIconMap: Record<ClaimType, string> = {
    none: "fa-cart-shopping",
    medical: "fa-suitcase-medical",
    pharmacy: "fa-capsules",
    dental: "fa-tooth",
    vision: "fa-glasses",
};
export const massTransitManualClaimStatusIcon: Record<MassTransitManualClaimStatus, string> = {
    inactive: "fa-light fa-octagon-xmark",
    active: "fa-light fa-octagon-check",
    willBeActiveOnFutureDate: "fa-light fa-clock"
};
const formDocumentTypeIconMap: Record<FormDocumentType, string> = {
    none: "fa-file",
    document: "fa-file",
    link: "fa-link"
};
export function formDocumentTypeIcon (docType: FormDocumentType): string {
    return formDocumentTypeIconMap[docType];
}
const expenseCategoryIconMap: Record<string, string> = {
    // --- ExpenseCategory ---
    none: "fa-cart-shopping",
    dental: "fa-tooth",
    medical: "fa-suitcase-medical",
    eye: "fa-glasses",
    pharmacy: "fa-capsules",
    other: "fa-cart-shopping",
    // --- HsabExpenseCategory ---
    unknown: "fa-circle-question",
    // medical: "fa-suitcase-medical", duplicate shared by both enums
    // dental is shared
    vision: "fa-eye",
    // pharmacy is shared
    dependentCare: "fa-child-reaching",
    parking: "fa-car-side",
    massTransit: "fa-bus",
    premium: "fa-money-bills",
    tuition: "fa-graduation-cap",
    lifestyle: "fa-dumbbell",
};
const expenseSourceIconMap: Record<string, string> = {
    unknown: "fa-circle-question",
    recurringExpense: "fa-repeat",
    carrierClaim: "fa-hospital-user",
    consumer: "fa-receipt",
    debitCard: "fa-credit-card",
    administrator: "fa-headset",
    import: "fa-file-import",
    disbursement: "fa-money-bill-transfer",
    migrated: "fa-gear",
};
export const docTypeIconMap: Record<HsabDocumentType, string> = {
    none: "fa-question",
    tax: "fa-file-invoice-dollar",
    nonHsaAccountSummary: "fa-file",
    accountSummary: "fa-piggy-bank",
};
export const wexClaimReceiptStatusIcon: Record<HsabExpenseDisplayStatus, string> = {
    none: "",
    active: "arrow-rotate-right",
    applied: "cloud-upload",
    cancelled: "ban",
    completed: "check",
    error: "error",
    partiallyPaid: "money-bill",
    paid: "bank",
    denied: "times-circle",
    hold: "pause-circle",
    locked: "lock",
    inactive: "ban",
    newReceiptNeeded: "info-circle",
    pending: "clock",
    pendingReceipt: "clock",
    pendingReimbursement: "clock",
    receiptOverdue: "hourglass-end",
    receiptNeeded: "edit",
    resubmitted: "recycle",
    scheduledReimbursement: "clock",
    submitted: "rocket",
    expired: "ban",
    unsubmitted: "diamond",
    markedAsPaid: "bank",
    repaid: "Repaid",
    repaymentDue: "Repayment Due",
    available: "Available",
};
export function hsaPlanTypeIcon (): string {
    return "fa-piggy-bank";
}

export function hsabAccountIcon (account: HsabAccountNametag): string {
    if (account.bankAccountType === HsabAccountType.Hsa) {
        return hsaPlanTypeIcon();
    } else {
        const subIcon = planSubTypeIconMap[account.planSubType];
        if (subIcon) {
            return subIcon;
        }
        return planTypeIconMap[account.planType];
    }
}

export function resourceDocumentIcon (formDocumentPlan: FormDocumentPlan): string {
    const subIcon = resourceFormPlanTypeIconMap[formDocumentPlan.resourceFormPlanType];
    if (subIcon) {
        return subIcon;
    }
    return planTypeIconMap[formDocumentPlan.planType];
}

export function claimTypeIcon (claimType: ClaimType): string {
    return claimTypeIconMap[claimType];
}

export function expenseCategoryIcon (claimType: ExpenseCategory | HsabExpenseCategory): string {
    return expenseCategoryIconMap[claimType];
}

export function expenseSourceIcon (source: HsabExpenseOriginator): string {
    return expenseSourceIconMap[source];
}